import { useEffect, useState } from "react";
import { getPageImage } from "../lib/store";

/**
 * Renders an admin-manageable image for a given (pagePath, sectionName).
 *
 * Looks up an override in Supabase `page_images` (public.select, admin-only
 * write — see supabase-schema.sql). If found, renders that Storage URL;
 * otherwise falls back to `defaultImage` (a curated Unsplash placeholder).
 * Admins replace `defaultImage` via /admin → "Medien & Seitenbilder"
 * without a code deploy.
 *
 * Renders a shimmer skeleton (same aspect ratio as the final image, via
 * `wrapperClassName`) while the CMS lookup and/or the image itself is
 * loading, so layout never shifts (CLS-safe). Any lookup or network error
 * is swallowed silently — a broken/missing image slot must never break
 * page rendering or spam the console.
 */
export function DynamicImage({
  pagePath,
  sectionName,
  defaultImage,
  alt,
  className = "",
  wrapperClassName = "",
}: {
  pagePath: string;
  sectionName: string;
  defaultImage: string;
  alt: string;
  /** Applied to the <img> itself (object-fit, rounding, hover transforms, etc.). */
  className?: string;
  /** Applied to the wrapping <div> that holds the skeleton + image (sizing/aspect-ratio). */
  wrapperClassName?: string;
}) {
  const [src, setSrc] = useState(defaultImage);
  const [resolvedAlt, setResolvedAlt] = useState(alt);
  const [cmsResolved, setCmsResolved] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    getPageImage(pagePath, sectionName)
      .then((row) => {
        if (!active) return;
        if (row) {
          setSrc(row.image_url);
          if (row.alt_text) setResolvedAlt(row.alt_text);
        }
      })
      .catch(() => {
        // Silent fallback to defaultImage — a missing/unreachable CMS
        // lookup must never break page rendering or log to console.
      })
      .finally(() => {
        if (active) setCmsResolved(true);
      });
    return () => {
      active = false;
    };
  }, [pagePath, sectionName]);

  const showSkeleton = !cmsResolved || !imgLoaded;

  return (
    <div className={`relative overflow-hidden ${wrapperClassName}`}>
      {showSkeleton && (
        <div
          aria-hidden="true"
          className="absolute inset-0 animate-pulse bg-gradient-to-r from-charcoal/5 via-charcoal/10 to-charcoal/5 bg-[length:200%_100%]"
        />
      )}
      <img
        src={src}
        alt={resolvedAlt}
        loading="lazy"
        onLoad={() => setImgLoaded(true)}
        onError={() => setImgLoaded(true)}
        className={`${className} transition-opacity duration-500 ${showSkeleton ? "opacity-0" : "opacity-100"}`}
      />
    </div>
  );
}
